import React, { useState, useEffect, useRef } from 'react';

/**
 * MapPicker Component
 * Allows users to drop a pin on Google Maps to select a location
 * Returns latitude and longitude as a JSON object
 * 
 * Props:
 * - value: { lat: number, lng: number } | null
 * - onChange: (location: { lat: number, lng: number } | null) => void
 * - apiKey: string (Google Maps API key)
 */
export default function MapPicker({ value, onChange, apiKey }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Default center (Caribbean region)
  const defaultCenter = { lat: 18.2208, lng: -66.5901 };

  // Load Google Maps script
  useEffect(() => {
    if (!apiKey) {
      setError('Google Maps API key is required');
      console.error('MapPicker: No API key provided');
      return;
    }

    console.log('MapPicker: Loading Google Maps with API key:', apiKey.substring(0, 10) + '...');

    // Check if already loaded
    if (window.google && window.google.maps) {
      console.log('MapPicker: Google Maps already loaded');
      setIsLoaded(true);
      return;
    }

    // Load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('MapPicker: Google Maps loaded successfully');
      setIsLoaded(true);
    };
    script.onerror = (e) => {
      console.error('MapPicker: Failed to load Google Maps script', e);
      setError('Failed to load Google Maps. Check console for details.');
    };
    document.head.appendChild(script);

    // Listen for Google Maps API errors
    window.gm_authFailure = () => {
      console.error('MapPicker: Google Maps authentication failed');
      setError('Google Maps API authentication failed. Check your API key and restrictions.');
    };

    return () => {
      // Cleanup if needed
    };
  }, [apiKey]);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;

    const center = value && value.lat && value.lng ? value : defaultCenter;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: value ? 15 : 8,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    // Add click listener to place marker
    map.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      placeMarker({ lat, lng });
      if (onChange) {
        onChange({ lat, lng });
      }
    });

    // Place initial marker if value exists
    if (value && value.lat && value.lng) {
      placeMarker(value);
    }
  }, [isLoaded]);

  // Update marker when value changes externally
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    if (value && value.lat && value.lng) {
      placeMarker(value);
      mapInstanceRef.current.setCenter(value);
    } else {
      // Remove marker if value is cleared
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    }
  }, [value]);

  const placeMarker = (location) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker
    const marker = new window.google.maps.Marker({
      position: location,
      map: mapInstanceRef.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    // Update location when marker is dragged
    marker.addListener('dragend', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      if (onChange) {
        onChange({ lat, lng });
      }
    });

    markerRef.current = marker;
  };

  const handleClear = () => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (onChange) {
      onChange(null);
    }
  };

  const handleRecenter = () => {
    if (mapInstanceRef.current && value && value.lat && value.lng) {
      mapInstanceRef.current.setCenter(value);
      mapInstanceRef.current.setZoom(15);
    }
  };

  if (error) {
    return (
      <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33' }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: '20px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', textAlign: 'center' }}>
        Loading map...
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '400px',
          borderRadius: '8px',
          border: '1px solid #ddd',
        }}
      />
      <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        {value && value.lat && value.lng && (
          <>
            <span style={{ fontSize: '13px', color: '#666' }}>
              📍 Lat: {value.lat.toFixed(6)}, Lng: {value.lng.toFixed(6)}
            </span>
            <button
              type="button"
              onClick={handleRecenter}
              style={{
                padding: '6px 12px',
                background: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              Recenter
            </button>
            <button
              type="button"
              onClick={handleClear}
              style={{
                padding: '6px 12px',
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#c33',
              }}
            >
              Clear Location
            </button>
          </>
        )}
        {(!value || !value.lat || !value.lng) && (
          <span style={{ fontSize: '13px', color: '#999' }}>
            Click on the map to place a pin
          </span>
        )}
      </div>
    </div>
  );
}
